import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';

import { config } from '@/config';
import {
  useGenerateEmailTokenMutation,
  useLazyGetMeQuery,
  useLoginMutation,
} from '@/features/auth/store/api';
import { needsOrgSetup, setToken, signOut, updateUser } from '@/features/auth/store/authSlice';
import { PrimaryButton, Input, FormField } from '@/lib/ui';
import { cn, extractServerError, extractServerErrorCode, notify } from '@/lib/utils';
import { ROUTES } from '@/routes';
import { useAppDispatch } from '@/store/hooks';

import { LOGIN_STATUS_PARAM, LOGIN_STATUS_PENDING } from '../constants';
import { LoginRequestSchema } from '../schemas';
import { createSocialAuthAttempt } from '../socialAuthState';
import type { LoginRequest, SocialProvider } from '../types';
import { AuthScreenShell } from './AuthScreenShell';
import { AuthSocialSection } from './AuthSocialSection';

const authFieldInputClass =
  'border-white/10 bg-black/40 text-white placeholder:text-white/45 ring-offset-background';

// ── Component ─────────────────────────────────────────────────────────────────

export function LoginForm() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [login, { isLoading }] = useLoginMutation();
  const [generateEmailToken] = useGenerateEmailTokenMutation();
  const [triggerGetMe] = useLazyGetMeQuery();

  const [showPassword, setShowPassword] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [pendingApproval, setPendingApproval] = useState(
    () => searchParams.get(LOGIN_STATUS_PARAM) === LOGIN_STATUS_PENDING,
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginRequest>({ resolver: zodResolver(LoginRequestSchema) });

  const onSubmit = async (data: LoginRequest) => {
    setNeedsVerification(false);
    setPendingApproval(false);
    try {
      const result = await login(data).unwrap();
      if (result.is_demo) {
        notify.error('Your account does not have access to the developer portal.');
        return;
      }
      dispatch(setToken(result.access_token));
      const me = await triggerGetMe().unwrap();
      dispatch(updateUser(me.user));
      navigate(needsOrgSetup(me.user) ? ROUTES.orgSetup : ROUTES.dashboard, { replace: true });
    } catch (err) {
      dispatch(signOut());
      switch (extractServerErrorCode(err)) {
        case 'ERR_EMAIL_NOT_VERIFIED':
          setNeedsVerification(true);
          setVerificationEmail(data.email);
          break;
        case 'ERR_ACCOUNT_NOT_ACTIVATED':
          setPendingApproval(true);
          break;
        default:
          notify.error(extractServerError(err, 'Login failed. Please check your credentials.'));
      }
    }
  };

  const handleResendVerification = async () => {
    try {
      await generateEmailToken({ email: verificationEmail }).unwrap();
      notify.success('Verification email sent. Check your inbox.');
      setNeedsVerification(false);
    } catch {
      notify.error('Failed to send verification email. Please try again.');
    }
  };

  const handleSocialLogin = async (provider: SocialProvider) => {
    const url = provider === 'google' ? config.googleAuthUrl : config.githubAuthUrl;
    const clientId = provider === 'google' ? config.googleClientId : config.githubClientId;
    if (!url) return;

    let attempt: Awaited<ReturnType<typeof createSocialAuthAttempt>>;
    try {
      attempt = await createSocialAuthAttempt(provider);
    } catch {
      notify.error('Could not start the sign-in. Please try again.');
      return;
    }
    const { state, codeChallenge } = attempt;
    const pkce = { code_challenge: codeChallenge, code_challenge_method: 'S256' };

    const params =
      provider === 'google'
        ? new URLSearchParams({
            client_id: clientId,
            redirect_uri: config.socialRedirectUri,
            response_type: 'code',
            scope: 'email profile',
            access_type: 'offline',
            prompt: 'select_account',
            state,
            ...pkce,
          })
        : new URLSearchParams({
            client_id: clientId,
            redirect_uri: config.socialRedirectUri,
            scope: 'user:email',
            state,
            ...pkce,
          });

    window.location.href = `${url}?${params.toString()}`;
  };

  const hasSocial = Boolean(config.googleAuthUrl || config.githubAuthUrl);

  return (
    <AuthScreenShell activeTab="login">
      <p className="text-sm text-white/60">Welcome back. Enter your credentials to continue.</p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <FormField
          label="Email"
          htmlFor="login-email"
          error={errors.email?.message}
          labelClassName="!text-white !font-semibold"
        >
          <Input
            id="login-email"
            type="email"
            placeholder="Enter your email"
            autoComplete="email"
            className={authFieldInputClass}
            {...register('email')}
          />
        </FormField>

        <FormField
          label="Password"
          htmlFor="login-password"
          error={errors.password?.message}
          labelClassName="!text-white !font-semibold"
        >
          <div className="relative">
            <Input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              autoComplete="current-password"
              className={cn('pr-10', authFieldInputClass)}
              {...register('password')}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((p) => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        </FormField>

        <div className="flex justify-end">
          <Link
            to={ROUTES.forgotPassword}
            className="text-xs font-medium text-primary transition-colors hover:text-primary-400"
          >
            Forgot password?
          </Link>
        </div>

        {pendingApproval && (
          <div role="status" className="rounded-lg border border-warning/30 bg-warning/10 p-3">
            <p className="text-sm text-warning-dark dark:text-warning">
              Your account is waiting for approval.
            </p>
            <p className="mt-1 text-xs text-white/60">
              Our team will reach out once it&apos;s activated. You can sign in after that.
            </p>
          </div>
        )}

        {needsVerification && (
          <div className="rounded-lg border border-warning/30 bg-warning/10 p-3">
            <p className="text-sm text-warning-dark dark:text-warning">
              Your email address is not verified.
            </p>
            <button
              type="button"
              onClick={handleResendVerification}
              className="mt-1 text-xs text-white/60 underline hover:text-white"
            >
              Resend verification email
            </button>
          </div>
        )}

        <PrimaryButton type="submit" className="w-full font-semibold" disabled={isLoading}>
          {isLoading ? 'Logging in…' : 'Login'}
        </PrimaryButton>
      </form>

      {hasSocial && (
        <AuthSocialSection
          onGoogle={() => handleSocialLogin('google')}
          onGithub={() => handleSocialLogin('github')}
        />
      )}

      <p className="text-center text-sm text-white/60">
        Don&apos;t have an account?{' '}
        <Link to={ROUTES.register} className="font-semibold text-white hover:underline">
          Create one
        </Link>
      </p>
    </AuthScreenShell>
  );
}
